/**
 * Gemeinsamer Rahmen von `IwmFormContainer.razor` und `IwmFormRechnung.razor`, seit REQ-27
 * als Inhalt einer Schublade: Der Rahmen zeichnet nur noch den Rumpf und **übergibt** Titel
 * und Fußleiste an seine Hülle (`shell`). Die Leiste steckt beides in ihren `Drawer` — der
 * eigene Refresh-Kopf des Altclients entfällt, die Schaltflächen stehen in der Fußleiste.
 */

import type { FormEvent, ReactNode } from 'react';

import { Button, ProgressButton, Spinner } from '@/components/ui';

export interface FormShellParts {
  /** Titel der Schublade, z. B. „Container MSCU1234567". */
  title: string;
  /** Fußleiste: „Refresh" links, „Cancel" und „Save" rechts. */
  footer: ReactNode;
  /** Rumpf: die Felder oder — solange geladen wird — die Ladezeile. */
  children: ReactNode;
}

/** Hülle um Rumpf und Fußleiste; die Seitenleisten liefern dafür ihren `Drawer`. */
export type FormShell = (parts: FormShellParts) => ReactNode;

export interface FormFrameProps {
  title: string;
  shell: FormShell;
  /** `false`, solange View oder Datensatz laden — dann steht die Ladezeile im Rumpf. */
  ready: boolean;
  saving: boolean;
  isValid: boolean;
  /** „Refresh" gibt es nur beim Bearbeiten (`id > 0`), wie im Altclient. */
  showRefresh: boolean;
  onRefresh: () => void;
  onSave: () => void;
  /**
   * Klick auf das gesperrte „Save". Die Schublade springt damit auf den Reiter mit dem
   * ersten unzulässigen Pflichtfeld (REQ-27 §2.2).
   */
  onBlockedSave?: () => void;
  onCancel: () => void;
  /** Die Felder, bei mehreren Reitern samt Reiterleiste. */
  children: ReactNode;
}

export function FormFrame(props: FormFrameProps) {
  // `EditForm` unterdrückt das Abschicken des Formulars; die Schaltfläche arbeitet über
  // ihren Klick-Rückruf.
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const footer = (
    <>
      {props.showRefresh ? (
        <Button
          content="Refresh"
          iconClass="e-icons e-refresh"
          className="iwm-form-refresh"
          onClick={props.onRefresh}
        />
      ) : null}
      <Button content="Cancel" onClick={props.onCancel} />
      {/* Ein gesperrtes `button` verschluckt den Klick; die Hülle nimmt ihn an seiner Stelle
          an und schickt den Benutzer zum fehlenden Pflichtfeld. */}
      <span className="iwm-form-save" onClick={props.isValid ? undefined : props.onBlockedSave}>
        <ProgressButton
          content="Save"
          primary
          progress={props.saving}
          disabled={!props.isValid}
          onClick={props.onSave}
        />
      </span>
    </>
  );

  return props.shell({
    title: props.title,
    footer,
    children: (
      <form className="iwm-entity-form" onSubmit={onSubmit} noValidate>
        {props.ready ? (
          props.children
        ) : (
          <div className="iwm-state-loading">
            <span className="iwm-state-spinner">
              <Spinner size={20} />
            </span>
            Wird geladen …
          </div>
        )}
      </form>
    ),
  });
}

export default FormFrame;
